import { type AdjustablePlacement, PLACEMENT_ADJUSTMENT_MAX } from "@adpilot/shared";
import { type FormEvent, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { UnitInput } from "@/components/patterns/UnitInput";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";

export interface PlacementAdjustmentField {
  placement: AdjustablePlacement;
  label: string;
  /** Prefilled percent; null leaves the field empty. */
  value: number | null;
}

export interface PlacementAdjustmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Batch mode: an empty field keeps its current value, and a hint says so. */
  batch: boolean;
  fields: readonly PlacementAdjustmentField[];
  pending: boolean;
  onSubmit: (placements: Partial<Record<AdjustablePlacement, number>>) => void;
}

/** 0–900 whole percents, as Amazon (and the reference) accept; anything else is invalid. */
export function parseAdjustment(raw: string): number | null {
  if (!/^\d+$/.test(raw.trim())) return null;
  const value = Number(raw);
  return value <= PLACEMENT_ADJUSTMENT_MAX ? value : null;
}

/** 调整广告位竞价 / 批量调整广告位竞价. */
export function PlacementAdjustmentDialog({
  open,
  onOpenChange,
  title,
  batch,
  fields,
  pending,
  onSubmit,
}: PlacementAdjustmentDialogProps) {
  const { t } = useTranslation("campaign");
  const text = (key: string, options?: Record<string, unknown>) =>
    t(`detail.bidAdjustment.edit.${key}`, options);
  const formId = useId();
  const [values, setValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  // Each opening starts from the row's current values.
  useEffect(() => {
    if (!open) return;
    setValues(
      Object.fromEntries(
        fields.map((field) => [field.placement, field.value === null ? "" : String(field.value)]),
      ),
    );
    setErrors({});
  }, [open, fields]);

  const invalid = (raw: string) => (raw.trim() === "" ? !batch : parseAdjustment(raw) === null);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const next = Object.fromEntries(
      fields.map((field) => [field.placement, invalid(values[field.placement] ?? "")]),
    );
    setErrors(next);
    if (Object.values(next).some(Boolean)) return;
    const placements: Partial<Record<AdjustablePlacement, number>> = {};
    for (const field of fields) {
      const value = parseAdjustment(values[field.placement] ?? "");
      if (value !== null) placements[field.placement] = value;
    }
    // Batch with every field empty: nothing to change.
    if (Object.keys(placements).length === 0) {
      onOpenChange(false);
      return;
    }
    onSubmit(placements);
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !pending && onOpenChange(next)}>
      <DialogPopup className="max-w-[420px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogPanel>
          <form id={formId} noValidate onSubmit={submit} className="flex flex-col gap-4">
            {batch ? (
              <p className="whitespace-pre-line text-xs leading-5 text-muted-foreground">
                {text("batchHint", { max: PLACEMENT_ADJUSTMENT_MAX })}
              </p>
            ) : null}
            {fields.map((field) => {
              const errorId = `${formId}-${field.placement}-error`;
              return (
                // biome-ignore lint/a11y/noLabelWithoutControl: UnitInput renders the input.
                <label
                  key={field.placement}
                  className="flex flex-col gap-1.5 text-xs font-medium text-ink"
                >
                  <span>
                    {batch ? null : (
                      <span className="mr-0.5 text-destructive" aria-hidden>
                        *
                      </span>
                    )}
                    {field.label}
                  </span>
                  <UnitInput
                    unit="%"
                    inputMode="numeric"
                    placeholder={`0 ~ ${PLACEMENT_ADJUSTMENT_MAX}`}
                    value={values[field.placement] ?? ""}
                    aria-label={field.label}
                    aria-required={!batch}
                    aria-invalid={errors[field.placement] || undefined}
                    aria-describedby={errors[field.placement] ? errorId : undefined}
                    onChange={(event) => {
                      // Kept as typed: stripping "1.5" to "15" would send a
                      // different bid than the user meant. Validation flags it.
                      const raw = event.target.value;
                      setValues((current) => ({ ...current, [field.placement]: raw }));
                    }}
                    onBlur={() =>
                      setErrors((current) => ({
                        ...current,
                        [field.placement]: invalid(values[field.placement] ?? ""),
                      }))
                    }
                  />
                  {errors[field.placement] ? (
                    <span id={errorId} className="text-[11px] font-normal text-destructive">
                      {text("rangeError", { max: PLACEMENT_ADJUSTMENT_MAX })}
                    </span>
                  ) : null}
                </label>
              );
            })}
          </form>
        </DialogPanel>
        <DialogFooter>
          <Button variant="outline" disabled={pending} onClick={() => onOpenChange(false)}>
            {text("cancel")}
          </Button>
          <Button type="submit" form={formId} disabled={pending}>
            {pending ? <Spinner /> : null}
            {text("apply")}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
